import type React from 'react';
import { useState, useMemo } from 'react';
import { ForgeFloatingActionButton, ForgeIcon, ForgeTooltip } from '@tylertech/forge-react';

import type InteractiveUser from 'common/types/users/interactiveUser';
import type { View } from 'common/types/view';
import { ScheduleSendReportModal } from 'common/components/ScheduleSendReport';
import { isStoryWithGeneratedUuids } from 'common/components/AssetActionBar/components/publication_action/utils';
import { I18n } from 'common/i18n';
import { getAllFilterWhereClauses, getAllFilterMetadata } from 'store/reducers/GridFilterReducer';
import { useSelector } from 'react-redux';
import { isTablePrintingImprovementForScheduledSendEnabled } from 'common/visualizations/helpers/VifSelectors';

interface Props {
  user: InteractiveUser | undefined;
  view: View | undefined;
}

const ScheduledReportButton: React.FC<Props> = ({ user, view }) => {
  const [scheduledSendModalVisible, setScheduledSendVisible] = useState(false);
  const allFilterWhereClauses = useSelector(getAllFilterWhereClauses);
  const allFilterMetadata = useSelector(getAllFilterMetadata);

  const isStoryWithCompleteUuids = useMemo(() => {
    return isStoryWithGeneratedUuids();
  }, []);

  const renderScheduledReportButton = () => {
    return (
      <ForgeFloatingActionButton
        mini
        data-testid="schedule-report-action-button"
        onClick={() => setScheduledSendVisible(true)}
        disabled={!isStoryWithCompleteUuids}
      >
        {!isStoryWithCompleteUuids && (
          <ForgeTooltip placement={'left'}>
            {I18n.t('embed.scheduled_delivery_disabled_message')}
          </ForgeTooltip>
        )}
        <ForgeIcon name="send_clock" aria-hidden="false" external external-type="extended" />
      </ForgeFloatingActionButton>
    );
  };

  const closeScheduledSendModal = () => {
    setScheduledSendVisible(false);
  };

  return (
    <>
      {user && view ? (
        <>
          {renderScheduledReportButton()}
          {scheduledSendModalVisible && (
            <ScheduleSendReportModal
              view={view}
              onDismiss={closeScheduledSendModal}
              scheduledSendModalVisible={scheduledSendModalVisible}
              setScheduledSendModalVisible={setScheduledSendVisible}
              currentUser={user}
              filterWhereClauses={allFilterWhereClauses}
              {...(isTablePrintingImprovementForScheduledSendEnabled() && {
                filterMetadata: allFilterMetadata,
              })}
            />
          )}
        </>
      ) : null}
    </>
  );
};

export default ScheduledReportButton;
